import type { FontLoader } from './FontLoader';
import type { WordStyle } from './WordStyle';

/**
 * A WordElementToken represents a part of a word which can be rendered with the same font and style.
 * WordElementTokens may be highlighted independently from other WordElementTokens.
 * WordElementTokens are contained in a WordToken.
 *
 */
export class WordElementToken {
    readonly classType: string = 'WordElementToken';
    #text: string = '';
    #isHighlighted: boolean = false;
    #font: FontLoader = null;
    #style: WordStyle = null;

    /**
     * Builds a WordElementToken with a given text and highlight status.
     */
    constructor(text: string, highlighted: boolean, font: FontLoader = null, style: WordStyle = null) {
        this.#text = text;
        this.#isHighlighted = highlighted;
        this.#font = font || null;
        this.#style = style || null;
    }

    /**
     * Gets the text of this token.
     */
    get text(): string {
        return this.#text;
    }

    /**
     * Set the text of the token.
     */
    set text(text: string) {
        this.#text = text;
    }

    /**
     * Returns true if the token should be highlighted, false otherwise.
     */
    get highlighted(): boolean {
        return this.#isHighlighted;
    }

    /**
     * Sets the highlight status of the token. Set this to true to highlight the token.
     */
    set highlighted(highlighted: boolean) {
        this.#isHighlighted = highlighted;
    }

    /**
     * Gets the font of this token.
     */
    get font(): FontLoader {
        return this.#font;
    }

    /**
     * Set the font of the token.
     */
    set font(font: FontLoader) {
        this.#font = font;
    }

    /**
     * Gets the word style of this token.
     */
    get style(): WordStyle {
        return this.#style;
    }

    /**
     * Set the word style of the token.
     */
    set style(style: WordStyle) {
        this.#style = style;
    }

    /**
     * Returns a new WordElementToken with a substring of the token's text
     *
     * @param start - zero-based index at which to begin extraction
     * @param end - extracts up to but not including end
     */
    slice(start: number, end: number): WordElementToken {
        const clampedStart = Math.max(0, start);
        const clampedEnd = Math.min(end, this.#text.length);
        const text = this.#text.slice(clampedStart, clampedEnd);

        return new WordElementToken(text, this.#isHighlighted, this.#font, this.#style);
    }
}
